import React, { useState } from 'react';
import { Edit2, Trash2, Key, Power, PowerOff, CheckCircle2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { AdminService } from '../../services/api';
import ResetPasswordModal from '../ResetPasswordModal';

const SupervisorDetails = ({ selectedSupervisor, clinics, onBack, onEdit, onDelete }) => {
  const userRole = Cookies.get('role');
  const [currentStatus, setCurrentStatus] = useState(selectedSupervisor.vendor_approve_status || 'A');
  const [statusLoading, setStatusLoading] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  const isActive = currentStatus === 'A';

  const handleToggleStatus = async () => {
    const nextStatus = isActive ? 'D' : 'A';
    const actionLabel = isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${actionLabel} this supervisor?`)) return;

    setStatusLoading(true);
    try {
      const response = await AdminService.setVendorStatus(selectedSupervisor.id, nextStatus);
      if (response && response.status === 1) {
        setCurrentStatus(nextStatus);
        selectedSupervisor.vendor_approve_status = nextStatus;
        setAlertMsg(`Supervisor ${isActive ? 'deactivated' : 'activated'} successfully!`);
        setTimeout(() => setAlertMsg(''), 4000);
      } else {
        alert(response?.msg || 'Failed to update supervisor status');
      }
    } catch (err) {
      alert(err.message || 'Error updating status');
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div className="detail-view">
      {alertMsg && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2">
          <CheckCircle2 size={16} />
          <span>{alertMsg}</span>
        </div>
      )}
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Supervisors
        </button>
        <div className="flex items-center gap-3">
          <h2>{selectedSupervisor.vendorName || selectedSupervisor.name}</h2>
          <span className={`status-pill ${isActive ? 'approved' : 'pending'}`}>
            <span className="dot" />
            {isActive ? 'Approved / Active' : 'Deactivated'}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {userRole === 'SuperAdmin' && (
            <>
              <button
                className={`create-btn flex items-center gap-1 ${
                  isActive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                onClick={handleToggleStatus}
                disabled={statusLoading}
              >
                {isActive ? <PowerOff size={16} /> : <Power size={16} />}
                {statusLoading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                className="create-btn flex items-center gap-1 bg-slate-700 hover:bg-slate-800"
                onClick={() => setIsResetModalOpen(true)}
              >
                <Key size={16} /> Reset Password
              </button>
              <button className="create-btn flex items-center gap-1" onClick={onEdit}>
                <Edit2 size={16} /> Edit
              </button>
              <button className="create-btn flex items-center gap-1 bg-red-500 hover:bg-red-600" onClick={onDelete}>
                <Trash2 size={16} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
      <ResetPasswordModal
        vendor={selectedSupervisor}
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onSuccess={() => setAlertMsg('Password reset successfully')}
      />
      <div className="detail-content">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Name:</span>
            <span>{selectedSupervisor.vendorName}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span>{(selectedSupervisor.vendorEmail)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Phone:</span>
            <span>{selectedSupervisor.vendorConNumber}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Role:</span>
            <span>{selectedSupervisor.role}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Department:</span>
            <span>{selectedSupervisor.department}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Experience:</span>
            <span>{selectedSupervisor.experience}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Clinic:</span>
            <span>{clinics.find((c) => c.id === selectedSupervisor.clinic_id)?.vendor_name || selectedSupervisor.clinic}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorDetails;